import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ISenderPerformanceRow } from './performanceMetrics';

const PAGE_SIZE = 10;

interface ISendersPerformanceProps {
  rows: ISenderPerformanceRow[];
  loading: boolean;
}

function ratio(part: number, whole: number): string {
  if (!whole) return '0%';
  // One decimal, as in the design ("16.8%"), without a trailing ".0".
  return `${Number(((part / whole) * 100).toFixed(1))}%`;
}

const RateCell: FunctionComponent<{ part: number; whole: number; tone: 'blue' | 'green' | 'purple' }> = ({ part, whole, tone }) => (
  <RateWrap>
    <RateCount>{part} / {whole}</RateCount>
    <RateBadge $tone={tone}>{ratio(part, whole)}</RateBadge>
  </RateWrap>
);

const SendersPerformance: FunctionComponent<ISendersPerformanceProps> = ({ rows, loading }) => {
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [rows]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const visible = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const firstShown = rows.length ? page * PAGE_SIZE + 1 : 0;
  const lastShown = Math.min(rows.length, (page + 1) * PAGE_SIZE);

  return (
    <Wrap>
      <TableScroll>
        <Table>
          <thead>
            <tr>
              <Th>Sender</Th>
              <Th>Status</Th>
              <Th>Connections accepted</Th>
              <Th>Message replies</Th>
              <Th>Open Profile / InMails replies</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => {
              const name = r.name || 'Unnamed sender';
              return (
                <tr key={r.identityId}>
                  <Td>
                    <SenderCell>
                      <Avatar aria-hidden>{name.charAt(0).toUpperCase()}</Avatar>
                      <SenderName>{name}{r.isMe ? ' (Me)' : ''}</SenderName>
                    </SenderCell>
                  </Td>
                  <Td><Muted>In {r.campaignCount} campaign{r.campaignCount === 1 ? '' : 's'}</Muted></Td>
                  <Td><RateCell part={r.connectionsAccepted} whole={r.connectionRequestsSent} tone="blue" /></Td>
                  <Td><RateCell part={r.linkedinReplies} whole={r.linkedinMessagesSent} tone="green" /></Td>
                  <Td><RateCell part={r.inmailReplies} whole={r.inmailsSent} tone="purple" /></Td>
                </tr>
              );
            })}
            {!visible.length && (
              <tr>
                <Td colSpan={5}><Empty>{loading ? null : 'No senders match these filters.'}</Empty></Td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableScroll>

      <Paginator>
        <Muted>Showing {firstShown}-{lastShown} of {rows.length}</Muted>
        <PagerButtons>
          <PagerButton type="button" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</PagerButton>
          <PagerButton type="button" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>Next</PagerButton>
        </PagerButtons>
      </Paginator>
    </Wrap>
  );
};

export default SendersPerformance;

const INTER = `'InterVariable', 'Inter', NunitoSans, sans-serif`;

const Wrap = styled.div`display: flex; flex-direction: column;`;

const TableScroll = styled.div`overflow-x: auto;`;

const Table = styled.table`
  width: 100%; border-collapse: collapse;
  border-bottom: 2px solid #EEEEEE;
  tbody tr + tr td { border-top: 1px solid #F2F4F7; }
`;

const Th = styled.th`
  padding: 4px 16px 12px; text-align: left; white-space: nowrap;
  font-family: ${INTER}; font-size: 14px; font-weight: 400; line-height: 20px;
  color: #6B7280;
  &:first-child { padding-left: 32px; }
`;

const Td = styled.td`
  padding: 8px 16px; vertical-align: middle; white-space: nowrap;
  &:first-child { padding-left: 32px; }
`;

const SenderCell = styled.div`display: flex; align-items: center; gap: 12px;`;

const Avatar = styled.span`
  width: 36px; height: 36px; flex-shrink: 0; border-radius: 100px;
  display: inline-flex; align-items: center; justify-content: center;
  background: #EEF3FF; color: #454DE5;
  font-family: NunitoSans; font-size: 14px; font-weight: 700;
`;

const SenderName = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; font-weight: 600; line-height: 20px;
  color: #111927;
`;

const Muted = styled.span`
  font-family: ${INTER}; font-size: 14px; line-height: 20px; color: #6B7280;
`;

const RateWrap = styled.div`display: flex; align-items: center; gap: 8px;`;

const RateCount = styled.span`
  font-family: ${INTER}; font-size: 14px; line-height: 20px; color: #414651;
`;

const TONES = {
  blue: { bg: '#F0F7FF', border: '#B2DEFF', color: '#2E8FFA' },
  green: { bg: '#EDFCF2', border: '#ABF0C7', color: '#057547' },
  purple: { bg: '#F5F2FF', border: '#D9D6FF', color: '#5926DB' },
};

const RateBadge = styled.span<{ $tone: keyof typeof TONES }>`
  padding: 2px 8px; border-radius: 6px; border: 1px solid ${({ $tone }) => TONES[$tone].border};
  background: ${({ $tone }) => TONES[$tone].bg}; color: ${({ $tone }) => TONES[$tone].color};
  font-family: ${INTER}; font-size: 14px; line-height: 20px;
`;

const Empty = styled.div`
  padding: 32px 0; text-align: center;
  display: flex; justify-content: center;
  font-family: NunitoSans; font-size: 14px; color: #8C96AD;
`;

const Paginator = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 12px 24px 12px 32px;
`;

const PagerButtons = styled.div`
  display: flex; border: 1px solid #E1E5EB; border-radius: 8px; overflow: hidden;
`;

const PagerButton = styled.button`
  height: 28px; padding: 0 16px; border: none; background: #FFFFFF;
  font-family: ${INTER}; font-size: 12px; line-height: 18px; color: #6B7280;
  cursor: pointer;
  & + & { border-left: 1px solid #E1E5EB; }
  &:hover:not(:disabled) { background: #F9FAFB; }
  &:disabled { opacity: 0.45; cursor: default; }
`;
